import {
  Button,
  Card,
  Empty,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  notification,
} from "antd";
import { CheckOutlined, ReloadOutlined } from "@ant-design/icons";
import { useContext, useEffect, useState } from "react";

import { Context } from "../../../context";
import { Fetch, Post } from "../../../fetch";
import { useBank } from "./BankContext";
import { QuestionEditor } from "./ReviewTab";

const { Column } = Table;
const { Text } = Typography;

const STATUS_COLOR = { draft: "warning", approved: "success", retired: "default" };

/** One CMS category at a time, so its drafts are not mixed with the whole bank. */
const CategoryQuestionsTab = () => {
  const { token, apiUrl } = useContext(Context);
  const bankInfo = useBank();

  const [categories, setCategories] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("draft");
  const [selected, setSelected] = useState([]);
  const [counter, setCounter] = useState(0);

  const SIZE = 20;
  const category = categories.find((row) => row.id === selectedId) || null;

  useEffect(() => {
    Fetch(`${apiUrl}/v4/quiz-config/?configKey=${bankInfo.configKey}`, token, (result) => {
      const rows = ((result && result.config && result.config.categories) || [])
        .filter((row) => row && row.id);
      setCategories(rows);
      setSelectedId((current) => current || (rows[0] && rows[0].id) || null);
    });
  }, [apiUrl, token, bankInfo.configKey, counter]);

  useEffect(() => {
    if (!category) {
      setQuestions([]);
      setTotal(0);
      return;
    }
    const params = new URLSearchParams({
      page: String(page),
      size: String(SIZE),
      bank: bankInfo.bank,
      category_id: category.id,
    });
    if (status) params.set("status", status);
    if (category.topic) params.set("topic", category.topic);
    Fetch(`${apiUrl}/v4/quiz-config/questions?${params}`, token, (result) => {
      if (result && result.questions) {
        setQuestions(result.questions);
        setTotal(result.total || 0);
        setSelected([]);
      }
    });
  }, [apiUrl, token, category, page, status, counter, bankInfo.bank]);

  const approveIds = (ids, doneMessage) => {
    if (!ids.length) {
      notification.info({ message: "No draft questions to approve" });
      return;
    }
    Post(
      `${apiUrl}/v4/quiz-config/questions/bulk-status`,
      token,
      { ids, status: "approved" },
      (result) => {
        if (result && result.success) {
          notification.success({ message: doneMessage || `${result.modified} approved` });
          setCounter((n) => n + 1);
        } else {
          notification.error({ message: "Approve failed" });
        }
      },
      () => notification.error({ message: "Approve failed" })
    );
  };

  const approveAllDrafts = () => {
    if (!category) return;
    const params = new URLSearchParams({
      page: "1",
      size: "200",
      status: "draft",
      bank: bankInfo.bank,
      category_id: category.id,
    });
    if (category.topic) params.set("topic", category.topic);
    Fetch(`${apiUrl}/v4/quiz-config/questions?${params}`, token, (result) => {
      const ids = ((result && result.questions) || []).map((row) => row._id).filter(Boolean);
      approveIds(ids, `${ids.length} draft question(s) approved`);
    });
  };

  if (!categories.length) {
    return (
      <Empty description="No categories yet. Add one on Config & Levels, then generate questions for it." />
    );
  }

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card title="Categories" size="small">
        <Space wrap>
          {categories.map((row) => (
            <Button
              key={row.id}
              type={row.id === selectedId ? "primary" : "default"}
              onClick={() => {
                setSelectedId(row.id);
                setPage(1);
                setStatus("draft");
              }}
            >
              {`${row.title || row.id} · ${row.section === "earn" ? "Earn" : "Quiz"}`}
            </Button>
          ))}
          <Button icon={<ReloadOutlined />} onClick={() => setCounter((n) => n + 1)} />
        </Space>
        {category && (
          <Text type="secondary" style={{ display: "block", marginTop: 8 }}>
            {category.topic ? `Topic: ${category.topic}` : "No topic set"}
            {category.enabled === false ? " · disabled" : ""}
          </Text>
        )}
      </Card>

      {category && (
        <Card title={`${category.title || category.id} (${total})`} size="small">
          <Space style={{ marginBottom: 12 }} wrap>
            <Select
              style={{ width: 140 }}
              value={status}
              onChange={(value) => {
                setStatus(value);
                setPage(1);
              }}
              options={[
                { value: "draft", label: "Drafts" },
                { value: "approved", label: "Approved" },
                { value: "retired", label: "Retired" },
                { value: "", label: "All" },
              ]}
            />
            <Button type="primary" icon={<CheckOutlined />} onClick={approveAllDrafts}>
              Approve all drafts
            </Button>
            <Button
              icon={<CheckOutlined />}
              disabled={!selected.length}
              onClick={() => approveIds(selected)}
            >
              Approve selected ({selected.length})
            </Button>
          </Space>
          <Table
            dataSource={questions.map((q) => ({ ...q, key: q._id }))}
            size="small"
            rowSelection={{ selectedRowKeys: selected, onChange: setSelected }}
            pagination={{
              current: page,
              pageSize: SIZE,
              total,
              onChange: setPage,
              showSizeChanger: false,
            }}
            expandable={{
              expandedRowRender: (row) => (
                <QuestionEditor
                  question={row}
                  apiUrl={apiUrl}
                  token={token}
                  onSaved={(updated) =>
                    setQuestions((rows) => rows.map((item) => (item._id === updated._id ? updated : item)))
                  }
                />
              ),
            }}
          >
            <Column title="Level" dataIndex="level" width={70} />
            <Column title="Question" dataIndex="question_hi" ellipsis />
            <Column
              title="Correct"
              width={220}
              ellipsis
              render={(_, row) => (row.options || [])[row.correct_index]}
            />
            <Column
              title="Status"
              width={110}
              render={(_, row) => (
                <Tag color={STATUS_COLOR[row.status] || "default"}>{row.status}</Tag>
              )}
            />
          </Table>
        </Card>
      )}
    </Space>
  );
};

export default CategoryQuestionsTab;
